"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Heart from "lucide-react/dist/esm/icons/heart";
import Share2 from "lucide-react/dist/esm/icons/share-2";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { AssetDetail } from "@/hooks/useAssetDetail";

interface AssetHeaderProps {
  asset: AssetDetail;
  isOwner: boolean;
  isInWishlist: boolean;
  isTogglingWishlist: boolean;
  onToggleWishlist: () => void;
  onShare: () => void;
}

export function AssetHeader({
  asset,
  isOwner,
  isInWishlist,
  isTogglingWishlist,
  onToggleWishlist,
  onShare,
}: AssetHeaderProps) {
  return (
    <div>
      {asset.category && (
        <Link
          href={`/browse?category=${asset.category.slug}`}
          className="text-sm text-primary hover:underline"
        >
          {asset.category.name}
        </Link>
      )}
      <div className="flex items-start justify-between gap-3 mt-1">
        <h1 className="text-2xl lg:text-3xl font-display font-bold">{asset.title}</h1>
        <div className="flex items-center gap-1.5 shrink-0 pt-1">
          {!isOwner && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleWishlist}
                  disabled={isTogglingWishlist}
                  aria-pressed={isInWishlist}
                  aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                  className="h-9 w-9"
                >
                  <Heart
                    className={cn(
                      "h-5 w-5 transition-colors",
                      isInWishlist && "fill-destructive text-destructive"
                    )}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isInWishlist ? "Remove from wishlist" : "Add to wishlist"}</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                aria-label="Copy share link"
                onClick={onShare}
              >
                <Share2 className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Copy share link</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {(asset.tags?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" aria-label="Tags">
          {asset.tags?.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Badges row */}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {asset.is_free && <Badge variant="success">Free</Badge>}
        {asset.featured && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-mono">
            Featured
          </span>
        )}
        {asset.seller ? (
          <Link
            href={`/store/${asset.seller.username || asset.seller.id}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            by{" "}
            <span className="text-foreground font-medium">
              {asset.seller.display_name || asset.seller.username || "Unknown creator"}
            </span>
          </Link>
        ) : (
          // Seller profile gone (deleted/anonymized account) — keep an honest
          // byline instead of silently dropping it.
          <span className="text-sm text-muted-foreground">by a former creator</span>
        )}
      </div>
    </div>
  );
}
